import { Component, Input, OnInit } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FormControl } from '@angular/forms';
import { History, LucideAngularModule } from 'lucide-angular';
import { ButtonComponent } from '@app/partials/form/button/button.component';
import { CheckboxComponent } from '@app/partials/form/checkbox/checkbox.component';
import { ClientAccessService } from '@app/events/shared/client-access.service';
import {
  ClientAccessContactModel,
  ClientAccessStateModel,
} from '@app/events/shared/client-access.model';
import { ToastService } from '@app/shared/toast/toast.service';

/**
 * TVG-43: Staff-facing Client Access panel on the Event Details tab.
 *
 * Visibility is gated by the host (event view) on the `crm-sales-flow` toggle
 * plus an admin|manager|staff role check, so this component never renders for
 * customers. It loads the access state on init, lets staff toggle portal
 * logins on/off, resend invitations and trigger password resets per contact,
 * and renders the audit trail as a newest-first stream.
 */
@Component({
  selector: 'app-client-access-panel',
  standalone: true,
  imports: [CommonModule, DatePipe, LucideAngularModule, ButtonComponent, CheckboxComponent],
  templateUrl: './client-access-panel.component.html',
})
export class ClientAccessPanelComponent implements OnInit {
  @Input({ required: true }) eventId!: number;

  protected readonly HistoryIcon = History;

  protected state: ClientAccessStateModel | null = null;
  protected loading = false;
  protected loadError: string | null = null;

  /** True while the enable/disable request is in-flight. */
  protected togglePending = false;

  /** Contact ids with an in-flight resend / password-reset request. */
  protected resendingContactIds = new Set<number>();
  protected resettingContactIds = new Set<number>();

  // Bound to the <app-checkbox> switch. We drive it manually so a failed
  // request can be reverted, and we never let user clicks fire the endpoint
  // while another request is pending.
  protected readonly enabledControl = new FormControl<boolean>(
    { value: false, disabled: true },
    { nonNullable: true },
  );

  constructor(
    private clientAccessService: ClientAccessService,
    private toastService: ToastService,
  ) {}

  ngOnInit(): void {
    this.enabledControl.valueChanges.subscribe((value) => {
      this.onToggle(value);
    });
    this.loadState();
  }

  protected loadState(): void {
    if (!this.eventId) {
      return;
    }

    this.loading = true;
    this.loadError = null;

    this.clientAccessService.getState(this.eventId).subscribe({
      next: (response) => {
        this.applyState(response.data);
        this.loading = false;
      },
      error: (error) => {
        this.loading = false;
        this.loadError = error?.error?.message || 'Failed to load client access';
      },
    });
  }

  private onToggle(value: boolean): void {
    // Ignore programmatic syncs and re-entrant clicks while a request runs.
    if (this.togglePending || !this.state || value === this.state.enabled) {
      return;
    }

    this.togglePending = true;
    this.enabledControl.disable({ emitEvent: false });

    const request = value
      ? this.clientAccessService.enable(this.eventId)
      : this.clientAccessService.disable(this.eventId);

    request.subscribe({
      next: (response) => {
        this.applyState(response.data);
        this.togglePending = false;
        this.toastService.success(value ? 'Client access enabled' : 'Client access disabled');
      },
      error: (error) => {
        this.togglePending = false;
        // Revert the switch to the last known good state.
        this.syncEnabledControl();
        this.enabledControl.enable({ emitEvent: false });
        this.toastService.error(error?.error?.message || 'Failed to update client access');
      },
    });
  }

  protected resendInvitation(contact: ClientAccessContactModel): void {
    if (!this.state?.enabled || this.resendingContactIds.has(contact.id)) {
      return;
    }

    this.resendingContactIds.add(contact.id);
    this.clientAccessService.resendInvitation(this.eventId, contact.id).subscribe({
      next: (response) => {
        this.applyState(response.data);
        this.resendingContactIds.delete(contact.id);
        this.toastService.success(`Invitation re-sent to ${contact.name}`);
      },
      error: (error) => {
        this.resendingContactIds.delete(contact.id);
        this.toastService.error(error?.error?.message || 'Failed to resend invitation');
      },
    });
  }

  protected sendPasswordReset(contact: ClientAccessContactModel): void {
    if (this.resettingContactIds.has(contact.id)) {
      return;
    }

    this.resettingContactIds.add(contact.id);
    this.clientAccessService.sendPasswordReset(this.eventId, contact.id).subscribe({
      next: (response) => {
        this.applyState(response.data);
        this.resettingContactIds.delete(contact.id);
        this.toastService.success(`Password reset sent to ${contact.name}`);
      },
      error: (error) => {
        this.resettingContactIds.delete(contact.id);
        this.toastService.error(error?.error?.message || 'Failed to send password reset');
      },
    });
  }

  protected isResending(contact: ClientAccessContactModel): boolean {
    return this.resendingContactIds.has(contact.id);
  }

  protected isResetting(contact: ClientAccessContactModel): boolean {
    return this.resettingContactIds.has(contact.id);
  }

  private applyState(state: ClientAccessStateModel): void {
    this.state = state;
    this.syncEnabledControl();
    if (this.togglePending) {
      this.enabledControl.disable({ emitEvent: false });
    } else {
      this.enabledControl.enable({ emitEvent: false });
    }
  }

  /** Push the server's enabled flag onto the switch without firing onToggle. */
  private syncEnabledControl(): void {
    this.enabledControl.setValue(this.state?.enabled ?? false, { emitEvent: false });
  }
}
